import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader, btnPrimary, colors, input, label as labelStyle } from '../../components/ui'
import { api, ApiError } from '../../services/api'
import { useAuth } from '../../hooks/use-auth'

interface CustomerOption {
  readonly id: string
  readonly name: string
}

interface CreateResponse {
  readonly id: string
  readonly name: string
  readonly environmentType: 'edge' | 'cloud' | 'other'
  readonly ownerOrganizationId: string
  readonly plaintextKey: string
  readonly displayedOnce: true
}

// Customer portal form to enroll a new environment + mint its enrollment
// key (the deployment's instance identity, used to authenticate heartbeats).
// Licensing is per-tenancy and handled separately. On success the plaintext
// key is displayed exactly once -- the customer MUST copy it before
// navigating away. Re-visiting the detail page shows only metadata. Lost the
// key? Rotate it from the detail view.
export function NewEnvironmentPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  // medflow_admin acts on behalf of a customer and must pick the owning org;
  // customer_admin always provisions under their own org (filled server-side).
  const isMedflow = user?.role === 'medflow_admin'
  const [name, setName] = useState('')
  const [environmentType, setEnvironmentType] = useState<'edge' | 'cloud' | 'other'>('edge')
  const [notes, setNotes] = useState('')
  const [expectsEdgeReport, setExpectsEdgeReport] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<CreateResponse | null>(null)
  const [copied, setCopied] = useState(false)
  const [customers, setCustomers] = useState<readonly CustomerOption[]>([])
  const [ownerOrganizationId, setOwnerOrganizationId] = useState('')

  useEffect(() => {
    if (!isMedflow) return
    api
      .get<readonly CustomerOption[]>('/fleet/customers')
      .then((res) => setCustomers(res.data ?? []))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to load customers'))
  }, [isMedflow])

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault()
    if (isMedflow && !ownerOrganizationId) {
      setError('Select a customer organization for this environment')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const res = await api.post<CreateResponse>('/fleet/environments', {
        name: name.trim(),
        environmentType,
        notes: notes.trim() || undefined,
        // medflow_admin must name the owning org; customer_admin omits it.
        ...(isMedflow ? { ownerOrganizationId } : {}),
        // Only send when false -- default-true on the server keeps existing
        // envs and standard edge installs unaffected.
        ...(expectsEdgeReport ? {} : { expectsEdgeReport: false }),
      })
      if (res.data) setResult(res.data)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create environment')
    } finally {
      setSubmitting(false)
    }
  }

  async function copyKey(): Promise<void> {
    if (!result) return
    try {
      await navigator.clipboard.writeText(result.plaintextKey)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  if (result) {
    const centralUrl = typeof window !== 'undefined' ? window.location.origin : ''
    return (
      <div>
        <PageHeader title="Environment enrolled" />
        <div
          style={{
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            padding: '1.5rem',
            maxWidth: '720px',
          }}
        >
          <div
            style={{
              background: '#fef3c7',
              border: '1px solid #fcd34d',
              color: '#92400e',
              padding: '0.75rem',
              borderRadius: '4px',
              fontSize: '14px',
              marginBottom: '1rem',
            }}
            data-testid="one-time-warning"
          >
            ⚠ This enrollment key is shown <strong>exactly once</strong>. Copy it now and paste it into the new
            deployment&apos;s onboarding wizard. If you lose it, rotate the key from this environment&apos;s detail
            page.
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <div style={{ fontSize: '13px', color: colors.textMuted, marginBottom: '4px' }}>Environment</div>
            <div style={{ fontSize: '15px', color: colors.textHeading }}>
              {result.name} ({result.environmentType})
            </div>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <div style={{ fontSize: '13px', color: colors.textMuted, marginBottom: '4px' }}>Central URL</div>
            <code style={{ fontSize: '14px', padding: '0.25rem 0.5rem', background: colors.bgMuted }}>
              {centralUrl}
            </code>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <div style={{ fontSize: '13px', color: colors.textMuted, marginBottom: '4px' }}>Enrollment key</div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: colors.bgMuted,
                padding: '0.75rem',
                borderRadius: '4px',
              }}
            >
              <code
                data-testid="plaintext-key"
                style={{ flex: 1, fontSize: '14px', wordBreak: 'break-all', fontFamily: 'monospace' }}
              >
                {result.plaintextKey}
              </code>
              <button type="button" onClick={copyKey} style={btnPrimary} data-testid="copy-key-button">
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
            <button type="button" onClick={() => navigate('/licenses')} style={btnPrimary}>
              Done
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <PageHeader title="New Environment" subtitle="Enroll a new deployment and mint its enrollment key" />
      {error && (
        <div
          style={{
            color: colors.danger,
            padding: '0.75rem',
            background: colors.bgError,
            border: `1px solid ${colors.borderError}`,
            borderRadius: '3px',
            marginBottom: '1rem',
          }}
        >
          {error}
        </div>
      )}
      <form
        onSubmit={handleSubmit}
        style={{
          maxWidth: '560px',
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '1.5rem',
        }}
      >
        {isMedflow && (
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Customer organization</label>
            <select
              required
              value={ownerOrganizationId}
              onChange={(e) => setOwnerOrganizationId(e.target.value)}
              style={input}
              data-testid="env-owner-org"
            >
              <option value="">-- Select customer --</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div style={{ marginBottom: '1rem' }}>
          <label style={labelStyle}>Name</label>
          <input
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme Lab — Main Site"
            style={input}
            data-testid="env-name"
          />
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label style={labelStyle}>Environment type</label>
          <select
            value={environmentType}
            onChange={(e) => setEnvironmentType(e.target.value as 'edge' | 'cloud' | 'other')}
            style={input}
            data-testid="env-type"
          >
            <option value="edge">Edge (on-premise SBC)</option>
            <option value="cloud">Cloud (managed)</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label style={labelStyle}>Notes (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            style={{ ...input, height: 'auto' }}
          />
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <label
            style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer' }}
            data-testid="expects-edge-report-label"
          >
            <input
              type="checkbox"
              checked={expectsEdgeReport}
              onChange={(e) => setExpectsEdgeReport(e.target.checked)}
              data-testid="expects-edge-report"
              style={{ marginTop: '3px' }}
            />
            <span>
              <div style={{ fontSize: '14px', color: colors.textHeading }}>AlphaEdge edge stack will report in</div>
              <div style={{ fontSize: '12px', color: colors.textMuted, marginTop: '2px' }}>
                Uncheck for split-mode / instrument-monitoring-only environments where UDM heartbeats to Central
                directly and no AlphaEdge edge stack is deployed yet. Edge-derived alerts (stale_deployment, container
                health, log bursts) are suppressed when unchecked; license-expiration alerts still fire. Toggle back on
                from the env detail page once AlphaEdge is deployed.
              </div>
            </span>
          </label>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button type="button" onClick={() => navigate('/licenses')}>
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || (isMedflow && !ownerOrganizationId)}
            style={btnPrimary}
            data-testid="submit-env"
          >
            {submitting ? 'Creating...' : 'Create & Generate Key'}
          </button>
        </div>
      </form>
    </div>
  )
}
